import React from 'react';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import { Column, ColumnEditorOptions, ColumnEvent } from 'primereact/column';
import { useCreateEpdDeclarationOfCO2Assumption, useUpdateEpdDeclarationOfCO2Assumption } from 'services/api/mutations';
import { useGetEpdDeclarationsOfCO2 } from 'services/api/queries';
import styled from 'styled-components';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { DeclarationOfCO2Model } from 'types/types';

import useIsReadOnlyMode from '../../hooks/useIsReadOnlyMode';
import { cellTextEditor } from '../GridCellEditors';
import { DataTableStyled, Placeholder } from '../StyledGrid';

const cellBodyStyle = {
  minHeight: 32,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 14,
};

const DeclarationOfCO2Grid: React.FunctionComponent<{
  epdVersionId: string;
  disabled: boolean;
  error: string | undefined;
}> = ({ epdVersionId, disabled, error }) => {
  const declarationsOfCO2 = useGetEpdDeclarationsOfCO2(epdVersionId).data;
  const createDeclarationOfCO2Assumption = useCreateEpdDeclarationOfCO2Assumption(epdVersionId);
  const updateDeclarationOfCO2Assumption = useUpdateEpdDeclarationOfCO2Assumption(epdVersionId);

  const isReadOnly = useIsReadOnlyMode() || disabled;

  const rowClassName = () => ({ 'p-disabled': isReadOnly });

  const selectedRows = declarationsOfCO2?.filter((d) => d.isSelected) ?? [];

  const createOrUpdateDeclarationOfCO2Assumption = (rowData: DeclarationOfCO2Model) => {
    const { nameId, assumptions, isSelected } = rowData;
    if (!rowData.epdVersionId) {
      createDeclarationOfCO2Assumption.mutate({
        nameId,
        epdVersionId,
        assumptions: assumptions ?? '',
        isSelected: true,
      });
      return;
    }
    updateDeclarationOfCO2Assumption.mutate({
      nameId,
      epdVersionId,
      assumptions: assumptions ?? '',
      isSelected: !isSelected,
    });
  };

  const handleRowClick = (event: any) => {
    const checkboxColumnIndex = 0;
    const target = event.originalEvent.target;
    const cell = target.closest('td');

    if (!cell || cell.cellIndex !== checkboxColumnIndex) return;
    const rowData = event.data;

    if (selectedRows.includes(rowData)) {
      createOrUpdateDeclarationOfCO2Assumption(rowData);
      return;
    }

    createOrUpdateDeclarationOfCO2Assumption(rowData);
  };

  const onCellEditComplete = (e: ColumnEvent) => {
    const { rowData, newValue } = e;
    const data = {
      epdVersionId,
      nameId: rowData.nameId,
      assumptions: newValue ?? '',
      isSelected:
        selectedRows.find((r) => r.nameId === rowData.nameId && r.epdVersionId === epdVersionId)?.isSelected ?? false,
    };
    if (!rowData.epdVersionId) {
      createDeclarationOfCO2Assumption.mutate(data);
      return;
    }
    updateDeclarationOfCO2Assumption.mutate(data);
  };

  const header = (
    <div className="flex flex-wrap align-items-center justify-content-start gap-2">
      <HeaderCaptionSemiBold className="white-space-nowrap">
        CO<sub>2</sub> uptake associated with carbonation and its assumptions
      </HeaderCaptionSemiBold>
      {error && <TooltipErrorIcon content={error} />}
    </div>
  );

  const renderNameColumnBody = (rowData: DeclarationOfCO2Model) => {
    const { name } = rowData;
    return <div style={cellBodyStyle}>{name}</div>;
  };

  const renderSelectedRowAssumptionCell = (assumptions: string | undefined) => {
    if (!assumptions) {
      return <Placeholder>Type here assumptions</Placeholder>;
    }
    return <span>{assumptions}</span>;
  };

  const renderAssumptionsColumnBody = (rowData: DeclarationOfCO2Model) => {
    const { assumptions } = rowData;
    return (
      <div style={cellBodyStyle}>{isRowSelected(rowData) ? renderSelectedRowAssumptionCell(assumptions) : 'Excluded'}</div>
    );
  };

  const assumptionsColumnCellEditor = (options: ColumnEditorOptions) => {
    if (isRowSelected(options.rowData)) {
      return cellTextEditor(options, 'Type here assumptions');
    }
    return <div style={cellBodyStyle}>Excluded</div>;
  };

  const isRowSelected = (rowData: DeclarationOfCO2Model) => {
    return (
      selectedRows.some((row) => row.epdVersionId === rowData.epdVersionId && row.nameId === rowData.nameId) && !isReadOnly
    );
  };

  const handleRowSelect = (event: any) => {
    const { data } = event;
    const selectData = {
      epdVersionId,
      nameId: data.nameId,
      assumptions: data.assumptions ?? '',
      isSelected: true,
    };
    if (!data.epdVersionId) {
      createDeclarationOfCO2Assumption.mutate(selectData);
      return;
    }
    updateDeclarationOfCO2Assumption.mutate(selectData);
  };

  const handleRowUnselect = (event: any) => {
    const { data } = event;
    const unselectData = {
      epdVersionId,
      nameId: data.nameId,
      assumptions: data.assumptions ?? '',
      isSelected: false,
    };
    if (!data.epdVersionId) {
      createDeclarationOfCO2Assumption.mutate(unselectData);
      return;
    }
    updateDeclarationOfCO2Assumption.mutate(unselectData);
  };

  return (
    <CustomDataTableStyled
      dataKey="nameId"
      header={header}
      value={declarationsOfCO2}
      editMode="cell"
      selectionMode="checkbox"
      selection={selectedRows}
      onRowSelect={handleRowSelect}
      onRowUnselect={handleRowUnselect}
      rowClassName={rowClassName}
      {...(!isReadOnly && { onRowClick: handleRowClick })}
      showGridlines
    >
      <Column
        selectionMode={isReadOnly ? undefined : 'multiple'}
        headerStyle={{ display: 'none' }}
        style={{ width: '5%' }}
        align="center"
      />
      <Column
        field="name"
        headerStyle={{ display: 'none', overflow: 'hidden', position: 'relative' }}
        bodyStyle={{ padding: 0 }}
        body={renderNameColumnBody}
      />
      <Column
        field="assumptions"
        headerStyle={{ display: 'none' }}
        style={{ width: '40%' }}
        bodyStyle={{ padding: 0 }}
        body={renderAssumptionsColumnBody}
        editor={isReadOnly ? undefined : assumptionsColumnCellEditor}
        onCellEditComplete={onCellEditComplete}
      />
    </CustomDataTableStyled>
  );
};

const CustomDataTableStyled = styled(DataTableStyled)`
  --row-selection-color: ${(props) => props.theme.colors.primaryWhite};

  & .p-datatable-tbody > tr {
    background-color: ${(props) => props.theme.colors.elementsTableRow};
  }

  & .p-datatable-tbody > tr.p-disabled {
    --row-selection-color: ${(props) => props.theme.colors.elementsTableRow};
  }
`;
export default DeclarationOfCO2Grid;
